import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { FiEdit2, FiCopy, FiDownload } from "react-icons/fi";
import axios from "axios";
import LoadingOverlay from "../components/LoadingOverlay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { flushSync } from "react-dom";
import { ReactComponent as InputsSVG } from "../asserts/logo/Inputs.svg";

// Helper function to detect A7 VISION archetype
const isVisionArchetype = (archetypeCode) => {
  if (!archetypeCode || typeof archetypeCode !== 'string') return false;
  const code = archetypeCode.toUpperCase();
  return code.includes('A7') || code.includes('VISION');
};

// Helper function to detect A9/A12 AGENTIC archetype
const isAgenticArchetype = (archetypeCode) => {
  if (!archetypeCode || typeof archetypeCode !== 'string') return false;
  const code = archetypeCode.toUpperCase();
  return code.includes('A9') || code.includes('A12') || code.includes('AGENTIC') || code.includes('MULTI');
};

// Vision Metrics organized by Risk Categories (from POC Excel Sheet2)
const VISION_METRICS = {
  'Accuracy': [
    { id: 'object_detection', name: 'Object Detection', threshold: 'conf≥0.3', description: 'Category Breakdown, Detections, Dominant Category, Object Counts, Objects Detected' },
    { id: 'face_detection', name: 'Face Detection', threshold: '≥0.60 Risk', description: 'Child Faces Detected, Crowd Density, Faces Detected, Largest Face Size, Privacy Risk' },
    { id: 'ocr_extraction', name: 'OCR Detection', threshold: '≥0.20 accept', description: 'Char Count, Confidence, Detected Brands, Detected Languages, Extraction Quality' },
    { id: 'scene_detection', name: 'Scene Detection', threshold: 'N/A', description: 'Advertising Confidence, Advertising Style, Brand Confidence, Brightness Level, Color Temperature' },
    { id: 'brand_logo_detection', name: 'Brand Logo Detection', threshold: 'Score based', description: 'Brands Detected, Confidence Scores, Detection Methods, Total Brands, Brand Score' },
  ],
  'Fairness': [
    { id: 'bias_detection', name: 'Bias Detection', threshold: 'Pass/Fail', description: 'Age Bias Count, Cultural Bias Detected, Gender Bias Detected, Racial Bias Count, Stereotype Count' },
    { id: 'sentiment_analysis', name: 'Sentiment Analysis', threshold: '≤0.75 Pass', description: 'Dominant Emotion, Sentiment Label, Sentiment Score, Mood, Emotion Scores' },
    { id: 'emotion_recognition', name: 'Emotion Recognition', threshold: 'Pass/Fail', description: 'Detector Available, Dominant Emotion, Emotions' },
    { id: 'language_detection', name: 'Language Detection', threshold: 'Confidence', description: 'Character Diversity, Detected Languages, Detected Scripts, Primary Script, Word Count' },
  ],
  'Security': [
    { id: 'weapon_detection', name: 'Weapon Detection', threshold: '≤0.75 Pass', description: 'Weapon Count, Weapon Detected, Weapon Type, Weapons Detected, Confidence Score' },
    { id: 'violence_detection', name: 'Violence Detection', threshold: '≤0.75 Pass', description: 'Fighting Detected, Raw Violence Score, Slapping Detected, Quarreling Detected, Violence Detected' },
    { id: 'deepfake_detection', name: 'Deepfake Detection', threshold: 'Pass/Fail', description: 'Authenticity Score, Boundary Artifacts, Compression Artifacts, Deepfake Detected, Manipulation Likelihood' },
  ],
  'Robustness': [
    { id: 'audio_deepfake_detection', name: 'Audio Deepfake Detection', threshold: 'Score based', description: 'Score, Compliance Status, Risk Level (CRITICAL/HIGH/MEDIUM/LOW)' },
    { id: 'misinformation_detection', name: 'Misinformation Detection', threshold: '<0.6 Safe', description: 'Extracted Text, Manipulation Indicators, Manipulation Score, Misinformation Detected, Text Analysis' },
    { id: 'spam_detection', name: 'Spam Detection', threshold: '>0.5 Spam', description: 'Detections, Module Name, Status, Spam Category, Detection Count' },
    { id: 'anomaly_detection', name: 'Anomaly Detection', threshold: 'Score based', description: 'Anomalies Detected, Anomaly Count, Anomaly Details, Status, Module Name' },
    { id: 'quality_assessment', name: 'Quality Assessment', threshold: 'Score based', description: 'Image quality metrics, sharpness, brightness, contrast issues' },
    { id: 'audio_quality_assessment', name: 'Audio Quality Assessment', threshold: '≥0.6 Medium', description: 'Content Type, Metrics, Quality Issues, Safe, Quality Category' },
  ],
  'Explainability': [
    { id: 'toxicity_detection', name: 'Toxicity Detection', threshold: '≤0.7 Pass', description: 'Context, Extracted Text, Is Toxic, Raw Score, Text Length' },
    { id: 'profanity_detection', name: 'Profanity Detection', threshold: 'Severity', description: 'Contains Profanity, Max Severity, Profanity Count, Profanity Detected, Severity Breakdown' },
    { id: 'audio_transcription', name: 'Audio Transcription', threshold: 'Quality', description: 'Score, Compliance Status, Risk Level' },
    { id: 'audio_emotion_detection', name: 'Audio Emotion Recognition', threshold: 'Anxiety', description: 'Score, Compliance Status, Risk Level (based on anxiety detection)' },
    { id: 'color_analysis', name: 'Color Analysis', threshold: 'N/A', description: 'Average Brightness, Average Saturation, Brightness Level, Color Balance, Color Count' },
    { id: 'readability_analysis', name: 'Readability Analysis', threshold: 'Grade level', description: 'Average Grade Level, Avg Words Per Sentence, Complexity Score, Difficult Words %, Flesch Reading Ease' },
  ],
  'Ethics': [
    { id: 'copyright_detection', name: 'Copyright Detection', threshold: '≥0.7 High', description: 'Copyright Detected, Copyright Keywords, Watermark Detected, Perceptual Hash, Metadata Copyright' },
    { id: 'privacy_pii_detection', name: 'Privacy PII Detection', threshold: '≤0.6 Pass', description: 'PII Detected, Total PII Count, Extracted Text, Text PII Found' },
    { id: 'accessibility_check', name: 'Accessibility Check', threshold: '≥0.6 High', description: 'Contrast Ratio, Issues Found, WCAG Standard, Compliant, Is Advertising' },
    { id: 'age_appropriateness', name: 'Age Appropriateness', threshold: 'Age Rating', description: 'Age Rating (G/PG/13+/18+), Content Type, Target Audience, Restrictions, Brand Context' },
  ],
  'Privacy': [
    { id: 'nsfw_detection', name: 'NSFW Detection', threshold: 'Threshold', description: 'Gore Score, Nudity Score, NSFW Detected, Safety Score, Violence Score' },
    { id: 'nudity_detection', name: 'Nudity Detection', threshold: 'Threshold', description: 'Content Rating, Critical Parts Count, Explicit Content Detected, Nudity Detected, Skin Exposure Level' },
  ],
};

// A9/A12 Agentic Metrics organized by Risk Categories
const AGENTIC_METRICS = {
  'Accuracy': [
    { id: 'M-001', name: 'Average Explainability Score', threshold: '≥90.00', unit: 'score', description: 'Measures how accurately model outputs can be explained and justified without ambiguity.' },
  ],
  'Explainability': [
    { id: 'M-002', name: 'Documentation Completion Rate', threshold: '≥90.00', unit: 'percentage', description: 'Measures completeness of required AI documentation.' },
    { id: 'M-003', name: 'Average Fields Completion', threshold: '≥12.00', unit: 'count', description: 'Measures consistency and depth of documentation across templates.' },
    { id: 'M-004', name: 'Audit Trail Availability', threshold: '≥95.00', unit: 'percentage', description: 'Measures availability of traceable logs for AI decisions.' },
    { id: 'M-005', name: 'Average Trace Time(seconds)', threshold: '≤5.00', unit: 'seconds', description: 'Measures time required to trace a specific AI decision.' },
    { id: 'M-006', name: 'Governance Compliance Rate', threshold: '≥95.00', unit: 'percentage', description: 'Measures adherence to governance and policy requirements.' },
    { id: 'M-007', name: 'Average Oversight Reviews', threshold: '≥3.00', unit: 'count', description: 'Measures adequacy of human oversight for high-risk decisions.' },
    { id: 'M-008', name: 'Human Review Rate', threshold: '≥80.00', unit: 'percentage', description: 'Measures proportion of high-risk outputs reviewed by humans.' },
    { id: 'M-009', name: 'Liability Review Rate', threshold: '≥90.00', unit: 'percentage', description: 'Measures effectiveness of liability review for AI decisions.' },
    { id: 'M-010', name: 'Clear Accountability Chain', threshold: '≥95.00', unit: 'percentage', description: 'Measures clarity of responsibility assignment for AI outcomes.' },
  ],
  'Fairness': [
    { id: 'M-011', name: 'Average Fairness Score', threshold: '≥90.00', unit: 'score', description: 'Measures fairness across protected demographic groups.' },
    { id: 'M-012', name: 'Bias-Free Rate', threshold: '≥90.00', unit: 'percentage', description: 'Measures proportion of decisions free from bias.' },
    { id: 'M-013', name: 'Equal Opportunity Score', threshold: '≥85.00', unit: 'score', description: 'Measures equality of true positive rates across groups.' },
    { id: 'M-014', name: 'Impact Analysis Completion', threshold: '≥95.00', unit: 'percentage', description: 'Measures completion of fairness impact assessments.' },
  ],
  'Security': [
    { id: 'M-015', name: 'Incident-Free Rate', threshold: '≥98.00', unit: 'percentage', description: 'Measures frequency of safety incidents caused by AI.' },
    { id: 'M-016', name: 'Failsafe Availability', threshold: '≥100.00', unit: 'percentage', description: 'Measures availability of fallback mechanisms.' },
    { id: 'M-017', name: 'Safety Test Pass Rate', threshold: '≥95.00', unit: 'percentage', description: 'Measures success rate of safety test cases.' },
  ],
  'Privacy': [
    { id: 'M-018', name: 'Privacy Compliance Rate', threshold: '≥100.00', unit: 'percentage', description: 'Measures compliance with data protection requirements.' },
    { id: 'M-019', name: 'Breach-Free Rate', threshold: '≥100.00', unit: 'percentage', description: 'Measures absence of privacy breaches.' },
    { id: 'M-020', name: 'User Consent Rate', threshold: '≥95.00', unit: 'percentage', description: 'Measures proportion of data processed with valid consent.' },
    { id: 'M-021', name: 'Data Encryption Rate', threshold: '≥100.00', unit: 'percentage', description: 'Measures encryption coverage for data at rest and in transit.' },
  ],
  'Robustness': [
    { id: 'M-022', name: 'Average Robustness Score', threshold: '≥90.00', unit: 'score', description: 'Measures system stability under stress and noise.' },
    { id: 'M-023', name: 'Adversarial Test Pass Rate', threshold: '≥90.00', unit: 'percentage', description: 'Measures resistance to adversarial inputs.' },
    { id: 'M-024', name: 'Integrity Checks Pass Rate', threshold: '≥98.00', unit: 'percentage', description: 'Measures data consistency through processing pipelines.' },
  ],
  'Ethics': [
    { id: 'M-025', name: 'Average Ethical Score', threshold: '≥90.00', unit: 'score', description: 'Measures alignment with ethical principles.' },
    { id: 'M-026', name: 'Guideline Compliance Rate', threshold: '≥95.00', unit: 'percentage', description: 'Measures adherence to ethical standards.' },
    { id: 'M-027', name: 'Public Trust Rating', threshold: '≥75.00', unit: 'score', description: 'Measures stakeholder trust in AI outcomes.' },
    { id: 'M-028', name: 'Value Alignment Rate', threshold: '≥90.00', unit: 'percentage', description: 'Measures alignment with organizational values.' },
    { id: 'M-029', name: 'Policy Compliance Rate', threshold: '≥95.00', unit: 'percentage', description: 'Measures compliance with internal policies.' },
    { id: 'M-030', name: 'Regulatory Compliance Rate', threshold: '≥100.00', unit: 'percentage', description: 'Measures adherence to AI laws and regulations.' },
    { id: 'M-031', name: 'Oversight Review Score', threshold: '≥90.00', unit: 'score', description: 'Measures effectiveness of board-level oversight.' },
    { id: 'M-032', name: 'Risk Management Score', threshold: '≥95.00', unit: 'score', description: 'Measures effectiveness of risk identification and mitigation.' },
  ],
};

// Detailed Threshold Configuration for NIST AI RMF Compliance Matrix
const THRESHOLD_CONFIG = {
  object_detection: {
    score: { value: 0.3, type: 'max', label: '< 0.3' },
    objects_detected: { value: 100, type: 'max', label: '< 100' },
    is_commercial: { value: null, type: 'info', label: 'N/A' },
    is_food_product: { value: null, type: 'info', label: 'N/A' },
    dominant_category: { value: null, type: 'info', label: 'N/A' },
    scene_complexity: { value: ['HIGH', 'VERY_HIGH'], type: 'not_in', label: "not in ['HIGH', 'VERY_HIGH']" },
  },
  face_detection: {
    score: { value: 0.6, type: 'max', label: '< 0.60' },
    faces_detected: { value: 50, type: 'max', label: '< 50' },
    privacy_risk: { value: ['HIGH', 'CRITICAL'], type: 'not_in', label: "not HIGH/CRITICAL" },
    child_faces_detected: { value: 10, type: 'max', label: '< 10' },
    crowd_density: { value: ['CROWDED'], type: 'not_in', label: "not CROWDED" },
  },
  ocr_extraction: {
    score: { value: 0.2, type: 'min', label: '> 0.20' },
    confidence: { value: 0.3, type: 'min', label: '> 0.30' },
    char_count: { value: null, type: 'info', label: 'N/A' },
    extraction_quality: { value: null, type: 'info', label: 'N/A' },
  },
  scene_detection: {
    score: { value: null, type: 'info', label: 'N/A' },
    scene_type: { value: null, type: 'info', label: 'N/A' },
    brightness_level: { value: null, type: 'info', label: 'N/A' },
    color_temperature: { value: null, type: 'info', label: 'N/A' },
  },
  brand_logo_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    brands_detected: { value: 10, type: 'max', label: '< 10' },
    total_brands: { value: 5, type: 'max', label: '< 5' },
  },
  weapon_detection: {
    score: { value: 0.75, type: 'max', label: '≤ 0.75' },
    weapon_detected: { value: false, type: 'equals', label: '= false' },
    weapon_count: { value: 0, type: 'equals', label: '= 0' },
  },
  violence_detection: {
    score: { value: 0.75, type: 'max', label: '≤ 0.75' },
    violence_detected: { value: false, type: 'equals', label: '= false' },
    fighting_detected: { value: false, type: 'equals', label: '= false' },
    slapping_detected: { value: false, type: 'equals', label: '= false' },
    quarreling_detected: { value: false, type: 'equals', label: '= false' },
  },
  deepfake_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    deepfake_detected: { value: false, type: 'equals', label: '= false' },
    authenticity_score: { value: 0.5, type: 'min', label: '> 0.50' },
    manipulation_likelihood: { value: 0.5, type: 'max', label: '< 0.50' },
  },
  nsfw_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    nsfw_detected: { value: false, type: 'equals', label: '= false' },
    nudity_score: { value: 0.6, type: 'max', label: '< 0.60' },
    violence_score: { value: 0.7, type: 'max', label: '< 0.70' },
    safety_score: { value: 0.3, type: 'min', label: '> 0.30' },
  },
  nudity_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    nudity_detected: { value: false, type: 'equals', label: '= false' },
    skin_exposure_level: { value: ['SIGNIFICANT', 'EXPLICIT'], type: 'not_in', label: "not SIGNIFICANT/EXPLICIT" },
    explicit_content_detected: { value: false, type: 'equals', label: '= false' },
  },
  bias_detection: {
    score: { value: 0.6, type: 'max', label: '< 0.60' },
    bias_detected: { value: false, type: 'equals', label: '= false' },
    racial_bias_count: { value: 0, type: 'equals', label: '= 0' },
    gender_bias_detected: { value: false, type: 'equals', label: '= false' },
  },
  sentiment_analysis: {
    score: { value: 0.75, type: 'max', label: '≤ 0.75' },
    sentiment_score: { value: -0.5, type: 'min', label: '> -0.50' },
    dominant_emotion: { value: ['THREATENING', 'ANGRY', 'FEARFUL'], type: 'not_in', label: "not THREATENING/ANGRY" },
  },
  emotion_recognition: {
    score: { value: null, type: 'info', label: 'N/A' },
    dominant_emotion: { value: ['ANGRY', 'FEAR', 'DISGUST'], type: 'not_in', label: "not ANGRY/FEAR/DISGUST" },
    detector_available: { value: null, type: 'info', label: 'N/A' },
  },
  toxicity_detection: {
    score: { value: 0.7, type: 'max', label: '≤ 0.70' },
    is_toxic: { value: false, type: 'equals', label: '= false' },
    raw_score: { value: 0.6, type: 'max', label: '< 0.60' },
  },
  profanity_detection: {
    score: { value: 0.5, type: 'max', label: '< 0.50' },
    profanity_detected: { value: false, type: 'equals', label: '= false' },
    profanity_count: { value: 0, type: 'equals', label: '= 0' },
    max_severity: { value: ['SEVERE'], type: 'not_in', label: "not SEVERE" },
  },
  privacy_pii_detection: {
    score: { value: 0.6, type: 'max', label: '≤ 0.60' },
    pii_detected: { value: false, type: 'equals', label: '= false' },
    total_pii_count: { value: 0, type: 'equals', label: '= 0' },
  },
  copyright_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    copyright_detected: { value: false, type: 'equals', label: '= false' },
    watermark_detected: { value: null, type: 'info', label: 'N/A' },
  },
  misinformation_detection: {
    score: { value: 0.6, type: 'max', label: '< 0.60' },
    misinformation_detected: { value: false, type: 'equals', label: '= false' },
    manipulation_score: { value: 0.6, type: 'max', label: '< 0.60' },
  },
  spam_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    spam_detected: { value: false, type: 'equals', label: '= false' },
  },
  quality_assessment: {
    score: { value: 0.5, type: 'max', label: '< 0.50' },
    quality_score: { value: 0.4, type: 'min', label: '> 0.40' },
    blur_detected: { value: false, type: 'equals', label: '= false' },
  },
  color_analysis: {
    score: { value: null, type: 'info', label: 'N/A' },
    brightness_level: { value: null, type: 'info', label: 'N/A' },
    average_brightness: { value: 0.2, type: 'min', label: '> 0.20' },
    average_saturation: { value: 0.2, type: 'min', label: '> 0.20' },
  },
  accessibility_check: {
    score: { value: 0.6, type: 'min', label: '> 0.60' },
    contrast_ratio: { value: 4.5, type: 'min', label: '> 4.5' },
    compliant: { value: true, type: 'equals', label: '= true' },
  },
  age_appropriateness: {
    score: { value: null, type: 'info', label: 'N/A' },
    age_rating: { value: null, type: 'info', label: 'N/A' },
    content_type: { value: null, type: 'info', label: 'N/A' },
  },
  readability_analysis: {
    score: { value: null, type: 'info', label: 'N/A' },
    flesch_reading_ease: { value: 60, type: 'min', label: '> 60' },
    average_grade_level: { value: 12, type: 'max', label: '< 12' },
  },
  language_detection: {
    score: { value: null, type: 'info', label: 'N/A' },
    detected_languages: { value: null, type: 'info', label: 'N/A' },
    primary_script: { value: null, type: 'info', label: 'N/A' },
  },
  anomaly_detection: {
    score: { value: 0.7, type: 'max', label: '< 0.70' },
    anomalies_detected: { value: false, type: 'equals', label: '= false' },
    anomaly_count: { value: 0, type: 'equals', label: '= 0' },
  },
};

// Helper function to check metric compliance
const checkMetricCompliance = (moduleId, metricName, obtainedValue) => {
  const moduleConfig = THRESHOLD_CONFIG[moduleId];
  if (!moduleConfig) return { status: 'N/A', remarks: 'Not monitored' };

  const metricConfig = moduleConfig[metricName];
  if (!metricConfig || metricConfig.type === 'info') {
    return { status: 'N/A', remarks: 'Not monitored', threshold: 'N/A' };
  }

  const { value, type, label } = metricConfig;
  let status = 'N/A';
  let remarks = 'Not monitored';

  if (obtainedValue === undefined || obtainedValue === null) {
    return { status: 'N/A', remarks: 'No data', threshold: label };
  }

  switch (type) {
    case 'max':
      status = obtainedValue <= value ? 'PASS' : 'FAIL';
      remarks = status === 'PASS' ? 'Within threshold' : 'Exceeds threshold';
      break;
    case 'min':
      status = obtainedValue >= value ? 'PASS' : 'FAIL';
      remarks = status === 'PASS' ? 'Within threshold' : 'Below threshold';
      break;
    case 'equals':
      status = obtainedValue === value ? 'PASS' : 'FAIL';
      remarks = status === 'PASS' ? 'Within threshold' : 'Does not match expected';
      break;
    case 'not_in':
      const inList = Array.isArray(value) && value.includes(obtainedValue);
      status = !inList ? 'PASS' : 'FAIL';
      remarks = status === 'PASS' ? 'Within threshold' : 'Contains restricted value';
      break;
    default:
      break;
  }

  return { status, remarks, threshold: label };
};

// Format value for display
const formatMetricValue = (value) => {
  if (value === undefined || value === null) return 'N/A';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value.toString() : value.toFixed(3);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '[]' : `[${value.length} items]`;
  }
  if (typeof value === 'object') return '{...}';
  return String(value);
};

export default function TestEvaluationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // const { projectId } = useParams();
  const projectId = useSelector((state) => state.project.currentProjectId);

  // Extract incoming state only once
  const {
    qaData = [],
    applicationSummary = {},
    useCases = [],
    dataEntriesCount = 0,
    RiskCount = 0,
    Metrics = []
  } = location.state || {};

  const fullTotal = qaData.length * (Metrics.length || 1);

  // Local state
  const [testDataset, setTestDataset] = useState(qaData);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState("");
  const [tabValue, setTabValue] = useState(0);
  const [testsGenerated, setTestsGenerated] = useState(false);
  const [testFramework, setTestFramework] = useState("Pytest");
  const [ScriptType, setScriptType] = useState("API");
  const [generatedTests, setGeneratedTests] = useState([]);
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState("");
  const [selectedTestIndex, setSelectedTestIndex] = useState(-1);
  const [applicationSummaryState, setApplicationSummaryState] = useState(applicationSummary);
  const [useCasesState, setUseCasesState] = useState(useCases);
  const [dataEntriesCountState, setDataEntriesCountState] = useState(dataEntriesCount);

  // --- A7 VISION Image Upload State ---
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [validationLoading, setValidationLoading] = useState(false);
  const fileInputRef = useRef(null);

  // --- A7 VISION Individual Metrics Selection State ---
  const [selectedMetrics, setSelectedMetrics] = useState(() => {
    // Initialize with all metrics selected
    const initialSelection = {};
    Object.values(VISION_METRICS).flat().forEach(metric => {
      initialSelection[metric.id] = true;
    });
    return initialSelection;
  });
  const [expandedCategories, setExpandedCategories] = useState({});

  // --- A9/A12 AGENTIC CSV Upload State ---
  const [uploadedCSV, setUploadedCSV] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  const [agenticValidationResults, setAgenticValidationResults] = useState([]);
  const [agenticValidationLoading, setAgenticValidationLoading] = useState(false);
  const csvInputRef = useRef(null);

  // --- A9/A12 AGENTIC Metrics Selection State ---
  const [selectedAgenticMetrics, setSelectedAgenticMetrics] = useState(() => {
    // Initialize with all agentic metrics selected
    const initialSelection = {};
    Object.values(AGENTIC_METRICS).flat().forEach(metric => {
      initialSelection[metric.id] = true;
    });
    return initialSelection;
  });
  const [expandedAgenticCategories, setExpandedAgenticCategories] = useState({});

  // --- A7 VISION Compliance Table State ---
  const [expandedModules, setExpandedModules] = useState({});
  const [complianceTabValue, setComplianceTabValue] = useState(0);

  // Get count of selected metrics
  const selectedMetricsCount = Object.values(selectedMetrics).filter(Boolean).length;
  const totalMetricsCount = Object.values(VISION_METRICS).flat().length;

  // Get count of selected agentic metrics
  const selectedAgenticMetricsCount = Object.values(selectedAgenticMetrics).filter(Boolean).length;
  const totalAgenticMetricsCount = Object.values(AGENTIC_METRICS).flat().length;

  // Check if current archetype is A7 VISION
  const isVision = isVisionArchetype(applicationSummaryState?.archetypeCode);

  // Check if current archetype is A9/A12 AGENTIC
  const isAgentic = isAgenticArchetype(applicationSummaryState?.archetypeCode);

  // --- Metric Selection Helper Functions ---
  const handleSelectAllMetrics = () => {
    const newSelection = {};
    Object.values(VISION_METRICS).flat().forEach(metric => {
      newSelection[metric.id] = true;
    });
    setSelectedMetrics(newSelection);
  };

  const handleDeselectAllMetrics = () => {
    const newSelection = {};
    Object.values(VISION_METRICS).flat().forEach(metric => {
      newSelection[metric.id] = false;
    });
    setSelectedMetrics(newSelection);
  };

  const handleToggleMetric = (metricId) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [metricId]: !prev[metricId]
    }));
  };

  const handleToggleCategory = (categoryName) => {
    const categoryMetrics = VISION_METRICS[categoryName] || [];
    const allSelected = categoryMetrics.every(m => selectedMetrics[m.id]);

    const newSelection = { ...selectedMetrics };
    categoryMetrics.forEach(metric => {
      newSelection[metric.id] = !allSelected;
    });
    setSelectedMetrics(newSelection);
  };

  const isCategoryFullySelected = (categoryName) => {
    const categoryMetrics = VISION_METRICS[categoryName] || [];
    return categoryMetrics.every(m => selectedMetrics[m.id]);
  };

  const isCategoryPartiallySelected = (categoryName) => {
    const categoryMetrics = VISION_METRICS[categoryName] || [];
    const selectedCount = categoryMetrics.filter(m => selectedMetrics[m.id]).length;
    return selectedCount > 0 && selectedCount < categoryMetrics.length;
  };

  const getSelectedMetricIds = () => {
    return Object.entries(selectedMetrics)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => id);
  };

  // Quick selection presets based on Risk Categories
  const handleSelectSecurityMetrics = () => {
    handleDeselectAllMetrics();
    // Security category: Weapon, Violence, Deepfake detection
    const securityIds = ['weapon_detection', 'violence_detection', 'deepfake_detection'];
    setSelectedMetrics(prev => {
      const newSelection = { ...prev };
      securityIds.forEach(id => { newSelection[id] = true; });
      return newSelection;
    });
  };

  const handleSelectAccuracyMetrics = () => {
    handleDeselectAllMetrics();
    // Accuracy category: Object, Face, OCR, Scene, Brand detection
    const accuracyIds = ['object_detection', 'face_detection', 'ocr_extraction', 'scene_detection', 'brand_logo_detection'];
    setSelectedMetrics(prev => {
      const newSelection = { ...prev };
      accuracyIds.forEach(id => { newSelection[id] = true; });
      return newSelection;
    });
  };

  const handleSelectPrivacyMetrics = () => {
    handleDeselectAllMetrics();
    // Privacy + Ethics category metrics
    const privacyIds = ['nsfw_detection', 'nudity_detection', 'privacy_pii_detection', 'copyright_detection', 'accessibility_check', 'age_appropriateness'];
    setSelectedMetrics(prev => {
      const newSelection = { ...prev };
      privacyIds.forEach(id => { newSelection[id] = true; });
      return newSelection;
    });
  };

  const handleSelectFairnessMetrics = () => {
    handleDeselectAllMetrics();
    // Fairness category: Bias, Sentiment, Emotion, Language
    const fairnessIds = ['bias_detection', 'sentiment_analysis', 'emotion_recognition', 'language_detection'];
    setSelectedMetrics(prev => {
      const newSelection = { ...prev };
      fairnessIds.forEach(id => { newSelection[id] = true; });
      return newSelection;
    });
  };

  const handleSelectRobustnessMetrics = () => {
    handleDeselectAllMetrics();
    // Robustness category: Misinformation, Spam, Anomaly, Quality
    const robustnessIds = ['misinformation_detection', 'spam_detection', 'anomaly_detection', 'quality_assessment', 'audio_deepfake_detection', 'audio_quality_assessment'];
    setSelectedMetrics(prev => {
      const newSelection = { ...prev };
      robustnessIds.forEach(id => { newSelection[id] = true; });
      return newSelection;
    });
  };

  // --- A9/A12 AGENTIC Metric Selection Helper Functions ---
  const handleSelectAllAgenticMetrics = () => {
    const newSelection = {};
    Object.values(AGENTIC_METRICS).flat().forEach(metric => {
      newSelection[metric.id] = true;
    });
    setSelectedAgenticMetrics(newSelection);
  };

  const handleDeselectAllAgenticMetrics = () => {
    const newSelection = {};
    Object.values(AGENTIC_METRICS).flat().forEach(metric => {
      newSelection[metric.id] = false;
    });
    setSelectedAgenticMetrics(newSelection);
  };

  const handleToggleAgenticMetric = (metricId) => {
    setSelectedAgenticMetrics(prev => ({
      ...prev,
      [metricId]: !prev[metricId]
    }));
  };

  const handleToggleAgenticCategory = (categoryName) => {
    const categoryMetrics = AGENTIC_METRICS[categoryName] || [];
    const allSelected = categoryMetrics.every(m => selectedAgenticMetrics[m.id]);

    const newSelection = { ...selectedAgenticMetrics };
    categoryMetrics.forEach(metric => {
      newSelection[metric.id] = !allSelected;
    });
    setSelectedAgenticMetrics(newSelection);
  };

  const isAgenticCategoryFullySelected = (categoryName) => {
    const categoryMetrics = AGENTIC_METRICS[categoryName] || [];
    return categoryMetrics.every(m => selectedAgenticMetrics[m.id]);
  };

  const isAgenticCategoryPartiallySelected = (categoryName) => {
    const categoryMetrics = AGENTIC_METRICS[categoryName] || [];
    const selectedCount = categoryMetrics.filter(m => selectedAgenticMetrics[m.id]).length;
    return selectedCount > 0 && selectedCount < categoryMetrics.length;
  };

  const getSelectedAgenticMetricIds = () => {
    return Object.entries(selectedAgenticMetrics)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => id);
  };

  // --- A9/A12 AGENTIC CSV Upload Handlers ---
  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a valid CSV file');
        return;
      }

      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        setError('CSV file size must be less than 50MB');
        return;
      }

      setUploadedCSV(file);
      setCsvFileName(file.name);
      setError('');
    }
  };

  const handleCSVDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleCSVDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a valid CSV file');
        return;
      }

      setUploadedCSV(file);
      setCsvFileName(file.name);
      setError('');
    }
  };

  const handleRemoveCSV = () => {
    setUploadedCSV(null);
    setCsvFileName('');
    setAgenticValidationResults([]);
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  // Run validation for A9/A12 AGENTIC
  const handleRunAgenticValidation = async () => {
    if (!uploadedCSV) {
      setError('Please upload a CSV file first');
      return;
    }

    const selectedIds = getSelectedAgenticMetricIds();
    if (selectedIds.length === 0) {
      setError('Please select at least one metric to run');
      return;
    }

    try {
      setAgenticValidationLoading(true);
      setError('');
      setTabValue(1); // Switch to execution tab

      const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";

      // Create form data for CSV upload
      const formData = new FormData();
      formData.append('csv', uploadedCSV);
      formData.append('project_id', projectId);
      formData.append('applicationSummary', JSON.stringify(applicationSummaryState));
      formData.append('selectedMetrics', JSON.stringify(selectedIds));

      console.log("📤 Sending AGENTIC validation request with CSV:", uploadedCSV.name);
      console.log("📤 Selected metrics:", selectedIds.length, "out of", totalAgenticMetricsCount);

      const response = await axios.post(
        `${baseUrl}/test-evaluation/agentic-validate`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log("📥 AGENTIC validation response:", response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || "Validation failed");
      }

      // Transform results to match the expected table format
      const rawResults = response.data.results || [];
      const transformedResults = rawResults.map((r) => ({
        use_case: r.risk_category || r.metric_id || 'Agentic Validation',
        metric: r.metric_name || r.metricName,
        score: r.score,
        threshold: r.threshold,
        status: r.status === 'PASS' ? 'Pass' : r.status === 'FAIL' ? 'Fail' : r.status,
        risk: r.risk_category,
        risk_level: r.risk_level,
        eval_type: 'Agentic AI',
        details: r.details,
        metric_id: r.metric_id,
        risk_name: r.risk_name,
        unit: r.unit,
      }));

      setAgenticValidationResults(transformedResults);
      setResults(transformedResults);
      setSummary(response.data.summary);

      // Simulate execution display
      await simulateExecution(transformedResults);

    } catch (err) {
      console.error("❌ AGENTIC validation error:", err);
      setError(err.response?.data?.message || err.message || "Validation failed");
    } finally {
      setAgenticValidationLoading(false);
    }
  };

// --- Simulation counters ---
const [totalCount, setTotalCount] = useState(0);
const [runningCount, setRunningCount] = useState(0);
const [completedCount, setCompletedCount] = useState(0);
const [passedCount, setPassedCount] = useState(0);
const [failedCount, setFailedCount] = useState(0);
const [isExecuting, setIsExecuting] = useState(false);
const [currentTestIndex, setCurrentTestIndex] = useState(0);
const [currentTestName, setCurrentTestName] = useState("");


  // Fetch test evaluation data from API on mount
  useEffect(() => {
    const fetchTestEvaluationData = async () => {
      if (!projectId) {
        setInitialLoading(false);
        return;
      }

      try {
        setLoading(true);
        setInitialLoading(true);
        const baseUrl =
          process.env.REACT_APP_API_URL || "https://validaite.io/api";

        const response = await axios.get(`${baseUrl}/test-evaluation`, {
          params: { project_id: projectId },
        });

        console.log("📥 Fetched test evaluation data:", response.data);

        if (response.data.success && response.data.data) {
          const data = response.data.data;

          // Update state with fetched data if not already set from location.state
          if (data.dataset && data.dataset.length > 0 && testDataset.length === 0) {
            setTestDataset(data.dataset);
            setDataEntriesCountState(data.dataset.length);
          }

          if (data.applicationSummary && Object.keys(data.applicationSummary).length > 0) {
            setApplicationSummaryState(data.applicationSummary);
          }

          if (data.useCases && data.useCases.length > 0) {
            setUseCasesState(data.useCases);
          }

          if (data.generatedTests && data.generatedTests.length > 0) {
            setGeneratedTests(data.generatedTests);
            setTestsGenerated(true);
            setSelectedTestIndex(0);
          }

          if (data.framework) {
            setTestFramework(data.framework);
          }

          if (data.scriptType) {
            setScriptType(data.scriptType);
          }

          if (data.results && data.results.length > 0) {
            setResults(data.results);
          }

          if (data.summary && Object.keys(data.summary).length > 0) {
            setSummary(data.summary);
          }
        }
      } catch (err) {
        console.error("❌ Error fetching test evaluation data:", err);
        // Don't set error state here as it's just initial load
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    fetchTestEvaluationData();
  }, [projectId]);

  // Previous stage display - use dynamic endpoints from Application Setup
  const dynamicEndpoints = applicationSummaryState?.apiEndpoints || [];
  const previousStage = {
    applicationName: applicationSummaryState?.applicationName || "N/A",
    archetypeCode: applicationSummaryState?.archetypeCode || "N/A",
    interfaceType: applicationSummaryState?.interfaceType || "API",
    apiEndpoints: dynamicEndpoints,
    apiEndpoint: dynamicEndpoints[0] || "", // Use first endpoint as primary
    dataEntries: dataEntriesCountState || testDataset.length,
    useCases: useCasesState,
  };

  // const previousStage = {
  //   applicationName: "Insurance Policy Assistant",
  //   archetypeCode: "A2-RAG-ENT-PII",
  //   interfaceType: "Hybrid",
  //   apiEndpoint: "/api/test-evaluation",
  //   dataEntries: 1,
  //   useCases: ["Policy Premium Inquiry"],
  // };

  // Placeholder export handler
  const handleExport = () => {
    // implement CSV/Excel export as needed
    console.log("Export clicked - implement export logic");
  };


  const simulateExecution = async (backendResults) => {

  // Validate backendResults
  if (!backendResults || !Array.isArray(backendResults) || backendResults.length === 0) {
    console.warn("⚠️ No results to simulate");
    flushSync(() => {
      setRunningCount(0);
      setCompletedCount(0);
      setPassedCount(0);
      setFailedCount(0);
      setTotalCount(0);
      setProgress(0);
      setCurrentTestIndex(0);
      setCurrentTestName("");
    });
    return;
  }

  // RESET UI
  flushSync(() => {
    setRunningCount(0);
    setCompletedCount(0);
    setPassedCount(0);
    setFailedCount(0);
    setTotalCount(backendResults.length);
    setProgress(0);
    setCurrentTestIndex(0);
    setCurrentTestName("");
  });

  const total = backendResults.length;
  let pass = 0;
  let fail = 0;

  for (let i = 0; i < total; i++) {
    const r = backendResults[i];

    flushSync(() => {
      setRunningCount(1);   // show running
      setCurrentTestIndex(i + 1);
      setCurrentTestName(r.use_case || r.metric || `Test ${i + 1}`);
    });

    await new Promise(res => setTimeout(res, 500));

    const isPass = r.status === "Pass";

    if (isPass) {
      pass++;
      flushSync(() => setPassedCount(pass));
    } else {
      fail++;
      flushSync(() => setFailedCount(fail));
    }

    flushSync(() => {
      setCompletedCount(i + 1);
      setProgress(Math.round(((i + 1) / total) * 100));
    });
  }

  flushSync(() => {
    setRunningCount(0);
    setCompletedCount(total);
    setProgress(100);
    setCurrentTestIndex(0);
    setCurrentTestName("");
  });
};

  // ============================================================================
  // A7 VISION - Image Upload Handlers
  // ============================================================================
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, WebP, or BMP)');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Image size must be less than 10MB');
        return;
      }

      setUploadedImage(file);
      setError('');

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const file = event.dataTransfer.files[0];
    if (file) {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a valid image file (JPEG, PNG, GIF, WebP, or BMP)');
        return;
      }

      setUploadedImage(file);
      setError('');

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    setValidationResults([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Run validation modules for A7 VISION
  const handleRunVisionValidation = async () => {
    if (!uploadedImage) {
      setError('Please upload an image first');
      return;
    }

    const selectedIds = getSelectedMetricIds();
    if (selectedIds.length === 0) {
      setError('Please select at least one metric to run');
      return;
    }

    try {
      setValidationLoading(true);
      setError('');
      setTabValue(1); // Switch to execution tab

      const baseUrl = process.env.REACT_APP_API_URL || "https://validaite.io/api";

      // Create form data for image upload
      const formData = new FormData();
      formData.append('image', uploadedImage);
      formData.append('project_id', projectId);
      formData.append('applicationSummary', JSON.stringify(applicationSummaryState));
      // Send selected metrics as JSON string
      formData.append('selectedMetrics', JSON.stringify(selectedIds));

      console.log("📤 Sending VISION validation request with image:", uploadedImage.name);
      console.log("📤 Selected metrics:", selectedIds.length, "out of", totalMetricsCount);

      const response = await axios.post(
        `${baseUrl}/test-evaluation/vision-validate`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      console.log("📥 VISION validation response:", response.data);

      if (!response.data.success) {
        throw new Error(response.data.message || "Validation failed");
      }

      // Transform results to match the expected table format
      const rawResults = response.data.results || [];
      const transformedResults = rawResults.map((r) => ({
        use_case: r.risk_category || r.module_id || 'Vision Validation',
        metric: r.metric_name || r.module_id,
        score: r.score,
        threshold: r.threshold,
        status: r.status === 'PASS' ? 'Pass' : r.status === 'FAIL' ? 'Fail' : r.status,
        risk: r.risk_category,
        risk_level: r.risk_level,
        eval_type: 'ML+Vision',
        details: r.details,
        // Keep original fields too
        module_id: r.module_id,
        compliance_status: r.compliance_status,
        execution_time: r.execution_time,
      }));

      console.log("📊 Transformed results:", transformedResults);

      // Set validation results
      setValidationResults(transformedResults);
      setSummary(response.data.summary || null);
      setResults(transformedResults);

      // Run simulation for visual feedback with transformed results
      setValidationLoading(false);
      setIsExecuting(true);
      await simulateExecution(transformedResults);
      setIsExecuting(false);

    } catch (err) {
      console.error("❌ Error running VISION validation:", err);
      setError(err.response?.data?.message || err.message || "Validation failed");
      setValidationResults([]);
    } finally {
      setValidationLoading(false);
    }
  };

  // Run selected tests handler
  // const handleRunSelectedTests = async () => {
  //   setLoading(true);
  //   setProgress(20);
  //   setError("");
  //   try {
  //     const baseUrl =
  //       process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

  //     const payload = {
  //       project_id: projectId,
  //       dataset: testDataset.length > 0 ? testDataset : qaData,
  //       apiEndpoint: previousStage.apiEndpoint,
  //       threshold: 0.7,
  //     };

  //     console.log("📤 Sending test evaluation payload:", payload);
  //     console.log("📤 Dataset size:", payload.dataset.length);
  //     console.log("📤 API Endpoint to test:", previousStage.apiEndpoint);

  //     const response = await axios.post(`${baseUrl}/test-evaluation`, payload);

  //     console.log("📥 Backend response:", response.data);

  //     if (!response.data.success) {
  //       throw new Error(response.data.message || "Evaluation failed");
  //     }

  //     setProgress(100);
  //     setSummary(response.data.summary);
  //     setResults(response.data.results);

  //     console.log("✅ Evaluation completed successfully");
  //     console.log("📊 Summary:", response.data.summary);
  //     console.log("📊 Results count:", response.data.results.length);
  //   } catch (err) {
  //     console.error("❌ Error running test evaluation:", err);
  //     setError(err.response?.data?.message || err.message || "Unknown error");
  //     setSummary(null);
  //     setResults([]);
  //   } finally {
  //     setLoading(false);
  //     // small delay to let progress UI show 100%
  //     setTimeout(() => setProgress(0), 400);
  //   }
  // };
    const handleRunSelectedTests = async () => {
  try {
    setLoading(true);
    setError("");

    const baseUrl =
      process.env.REACT_APP_API_URL || "https://validaite.io/api";

    // Validate dataset before making API call
    const dataset = testDataset.length > 0 ? testDataset : qaData;
    if (!dataset || dataset.length === 0) {
      throw new Error("No test data available. Please ensure dataset is loaded before running tests.");
    }

    // Validate API endpoints
    if (!previousStage.apiEndpoint && (!previousStage.apiEndpoints || previousStage.apiEndpoints.length === 0)) {
      throw new Error("API endpoints are not configured. Please add endpoints in Application Setup (Step 2).");
    }

    // Validate project ID
    if (!projectId) {
      throw new Error("Project ID is missing. Please ensure a project is selected.");
    }

    const payload = {
      project_id: projectId,
      dataset: dataset,
      apiEndpoint: previousStage.apiEndpoint,
      apiEndpoints: previousStage.apiEndpoints || [],
      threshold: 0.7,
    };

    console.log("📤 Sending test evaluation payload:", {
      project_id: projectId,
      datasetSize: dataset.length,
      apiEndpoint: previousStage.apiEndpoint,
      apiEndpoints: previousStage.apiEndpoints,
    });

    const response = await axios.post(
      `${baseUrl}/test-evaluation`,
      payload
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Evaluation failed");
    }

    console.log("Test evaluation completed successfully");

    // Save backend results
    setSummary(response.data.summary);
    setResults(response.data.results);

    return response.data.results || []; // VERY IMPORTANT - ensure we always return an array
  } catch (err) {
    console.error("Error running test evaluation:", err);
    setError(err.response?.data?.message || err.message || "Failed to run tests");
    setSummary(null);
    setResults([]);
    return [];
  } finally {
    // setLoading(false);  <-- DO NOT REMOVE OVERLAY TOO EARLY
  }
};

  return (
    <Box sx={{ bgcolor: "#F6F3F2", minHeight: "100vh", overflowX: "hidden", width: "100%" }}>
      {/* Fixed Header */}
      <Box
        sx={{
          position: "fixed",
          top: 64,
          left: { xs: 0, md: "80px" },
          right: 0,
          zIndex: 120,
          bgcolor: "#F6F3F2",
          py: 2.5,
          px: 3,
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Typography variant="h5" fontWeight={600} color="#111827">
          Test Lab
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Automated GenAI Validation Pipeline & Scenario Test Suite...
        </Typography>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ p: 3, pt: 2, mt: "110px" }}>
        {/* Inputs from Previous Stage Card */}
        <Card
          sx={{
            mb: 3,
            boxShadow: "none",
            border: "1px solid #e0e0e0",
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 20,
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <InputsSVG style={{ width: 22, height: 22, marginRight: 8 }} />
                  <Typography variant="h6" fontWeight={700}>
                    Inputs from Previous Stages
                  </Typography>
              </Box>
              {/* <Typography variant="h6" fontWeight={600}>
                Inputs from Previous Stage
              </Typography> */}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <Typography color="text.secondary" fontWeight={400}>
                  Application
                </Typography>
                <Typography fontWeight={700}>
                  {previousStage.applicationName}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="text.secondary" fontWeight={400}>
                  Archetype Code
                </Typography>
                <Typography fontWeight={700}>
                  {previousStage.archetypeCode}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography color="text.secondary" fontWeight={400} mb={1}>
                  {isVision || isAgentic ? "API Endpoints" : "API Endpoints"}
                </Typography>
                {isVision || isAgentic ? (
                  <Typography fontWeight={700} color="text.secondary">
                    NA
                  </Typography>
                ) : (
                  previousStage.apiEndpoints.map((endpoint, index) => (
                    <Typography
                      key={index}
                      fontWeight={700}
                      color="primary"
                      sx={{
                        fontSize: "14px",
                        mb: index < previousStage.apiEndpoints.length - 1 ? 0.5 : 0,
                        wordBreak: "break-all",
                      }}
                    >
                      {endpoint}
                    </Typography>
                  ))
                )}
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography color="text.secondary" fontWeight={400}>
                  {isVision ? "Validation Modules" : isAgentic ? "Validation Metrics" : "Selected Tests"}
                </Typography>
                <Typography fontWeight={700}>
                  {isVision ? "30 Modules" : isAgentic ? "32 Metrics" : previousStage.dataEntries}
                </Typography>
              </Grid>
            </Grid>

            {/* <Grid container spacing={2} mt={1} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography fontWeight={700} variant="body1" color="text.primary">
                  Use Cases:
                </Typography>
                {(previousStage.useCases || []).map((uc, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      bgcolor: "#e7f1ff",
                      color: "#0a6cff",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      fontWeight: 700,
                      fontSize: 16,
                      boxShadow: "none",
                    }}
                  >
                    {uc.title || uc.name || `UseCase-${idx + 1}`}

                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid> */}
          </CardContent>
        </Card>

        {/* Automation Framework Configuration Card - Hidden for A7 VISION and A9/A12 AGENTIC */}
        {!isVision && !isAgentic && (
        <Card sx={{ mb: 3,
              boxShadow: "none",             // ⭐ remove shadow
              border: "1px solid #e0e0e0",    // ⭐ optional: match Figma card outline
              borderRadius: 2,

        }}>


          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Automation Framework Configuration
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="test-framework-label">
                    Test Framework
                    <Typography
                      component="span"
                      sx={{
                        color: "error.main",
                        fontSize: "inherit",
                        ml: 0.3,
                        mr: 3,
                      }}
                    >
                      *
                    </Typography>
                  </InputLabel>
                  <Select
                    labelId="test-framework-label"
                    id="test-framework"
                    value={testFramework}
                    label="Test Framework"
                    onChange={(e) => setTestFramework(e.target.value)}
                  >
                    <MenuItem value="Pytest">Pytest</MenuItem>
                    <MenuItem value="Cypress">Cypress</MenuItem>
                    <MenuItem value="Selenium">Selenium</MenuItem>
                    <MenuItem value="Playwright">Playwright</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="script-type-label">
                    Script Type
                    <Typography
                      component="span"
                      sx={{ color: "error.main", fontSize: "inherit", ml: 0.3 }}
                    >
                      *
                    </Typography>
                  </InputLabel>
                  <Select
                    labelId="script-type-label"
                    id="script-type"
                    value={ScriptType}
                    label="Script Type"
                    onChange={(e) => setScriptType(e.target.value)}
                  >
                    <MenuItem value="API">API</MenuItem>
                    <MenuItem value="UI">UI</MenuItem>
                    <MenuItem value="Hybrid">Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        )}

         {/* API Endpoints Display - only if ScriptType is API - Hidden for A7 VISION and A9/A12 AGENTIC */}
{!isVision && !isAgentic && ScriptType === "API" && (
  <Box sx={{ mb: 2, mt: 2 }}>
    <Paper
      elevation={0}
      sx={{
        background: "#FFFFFF",
        p: 2,
        borderRadius: 2,
        border: "0.75px solid #D9D9D9",
        boxShadow: "none",
        width: "100%",
      }}
    >
      <Typography
        sx={{
          color: "#000000",
          fontSize: 14,
          fontWeight: 600,
          mb: 1,
        }}
      >
        API Endpoints:
      </Typography>

      {previousStage.apiEndpoints.map((endpoint, index) => (
        <Typography
          key={index}
          sx={{
            fontWeight: 700,
            fontSize: 14,
            color: "#0094FB",
            wordBreak: "break-all",
            mb: index < previousStage.apiEndpoints.length - 1 ? 0.5 : 0,
          }}
        >
          {endpoint}
        </Typography>
      ))}
    </Paper>
  </Box>
)}
 

        {/* Generate via LLM Button - Hidden for A7 VISION and A9/A12 AGENTIC */}
        {!isVision && !isAgentic && (
        <Box mb={3} textAlign="left">
          <Button
            variant="outlined"
            sx={{
              width: "auto",
              minWidth: "220px",
              height: "40px",
              padding: "8px 24px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              backgroundColor: "rgba(0,148,251,0.5)",
              border: "none",
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: "16px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#0094FB",
                borderColor: "#0094FB",
              },
            }}
            disabled={tabValue !== 0 || genLoading}
            onClick={async () => {
              try {
                setGenLoading(true);
                setGenError("");

                const baseUrl =
                  process.env.REACT_APP_API_URL ||
                  "https://validaite.io/api";

                const payload = {
                  project_id: projectId,
                  framework: testFramework,
                  scriptType: ScriptType,
                  apiEndpoint: previousStage.apiEndpoint,
                  dataset: testDataset.length > 0 ? testDataset : qaData,
                  applicationSummary: applicationSummaryState,
                  useCases: useCasesState,
                };

                console.log("📤 Sending test generation request:", payload);

                const response = await axios.post(
                  `${baseUrl}/test-evaluation/generate`,
                  payload
                );

                console.log("📥 Generated tests response:", response.data);

                if (response.data.success && response.data.generatedTests) {
                  setGeneratedTests(response.data.generatedTests);
                  setTestsGenerated(true);
                  setSelectedTestIndex(
                    response.data.generatedTests.length > 0 ? 0 : -1
                  );
                } else {
                  setGenError(
                    response.data.message || "Failed to generate tests"
                  );
                }
              } catch (error) {
                console.error("❌ Error generating tests:", error);
                setGenError(
                  error.response?.data?.message ||
                    error.message ||
                    "Failed to generate tests"
                );
              } finally {
                setGenLoading(false);
              }
            }}
          >
            {genLoading ? "Generating..." : "Generate Test Scripts"}
          </Button>
        </Box>
        )}

        {!isVision && !isAgentic && genError && (
          <Box mb={2}>
            <Typography color="error">{genError}</Typography>
          </Box>
        )}

        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { display: "none" } }} // remove default underline
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",

             "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 600,
      fontSize: "16px",
      color: "#000",               // always black
      padding: "12px 0",
      minHeight: "44px",
      minWidth: "200px",
      maxWidth: "633px",
      flex: 1,
      borderRadius: "6px",
      backgroundColor: "transparent",
      border: "none",              // no borders for inactive
    },

            "& .Mui-selected": {
              backgroundColor: "#E1F5FF", // blue fill
              color: "#000000",

              // ⭐ ONLY bottom blue border (Figma style)
              borderBottom: "2px solid #0094FB",
            },
          }}
        >
          <Tab label={isVision ? "Media Upload" : isAgentic ? "CSV Upload" : "Test Scripts Generation"} />
          <Tab label={isVision ? "Validation Results" : isAgentic ? "Validation Results" : "Test Execution"} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* ---- TAB 0: TEST SCRIPTS GENERATION (RAG) or MEDIA UPLOAD (VISION) ---- */}
          {tabValue === 0 && (
            <Box>
              {/* Error display */}
              {error && (
                <Box
                  mb={3}
                  sx={{
                    p: 2,
                    bgcolor: "#ffeaea",
                    borderRadius: 2,
                    border: "1px solid #D90000",
                  }}
                >
                  <Typography color="#D90000" fontWeight={600}>
                    ❌ Error: {error}
                  </Typography>
                </Box>
              )}

              {/* ============================================================================ */}
              {/* A7 VISION - Image Upload UI with Individual Metric Selection */}
              {/* ============================================================================ */}
              {isVision ? (
                <Box>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Upload Media & Select Metrics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Upload an image and select specific metrics to validate. Supported formats: JPEG, PNG, GIF, WebP, BMP (max 10MB)
                  </Typography>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp,image/bmp"
                    style={{ display: 'none' }}
                  />

                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    {/* Upload Area */}
                    <Paper
                      elevation={0}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onClick={() => !uploadedImage && fileInputRef.current?.click()}
                      sx={{
                        flex: 1,
                        minHeight: 300,
                        borderRadius: 3,
                        border: uploadedImage ? "2px solid #18B76C" : "2px dashed #B6E6FB",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 4,
                        bgcolor: uploadedImage ? "#f0fff4" : "#F0F9FF",
                        cursor: uploadedImage ? "default" : "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: uploadedImage ? "#f0fff4" : "#E1F5FF",
                          borderColor: uploadedImage ? "#18B76C" : "#0094FB",
                        },
                      }}
                    >
                      {!uploadedImage ? (
                        <>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              bgcolor: "#E1F5FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 2,
                            }}
                          >
                            <CloudUploadIcon sx={{ fontSize: 40, color: "#0094FB" }} />
                          </Box>
                          <Typography fontWeight={700} fontSize={18} mb={1}>
                            Drag & Drop Image Here
                          </Typography>
                          <Typography color="text.secondary" variant="body2" mb={2}>
                            or click to browse files
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            sx={{
                              textTransform: "none",
                              borderColor: "#0094FB",
                              color: "#0094FB",
                              "&:hover": {
                                bgcolor: "#E1F5FF",
                                borderColor: "#0094FB",
                              },
                            }}
                          >
                            Select Image
                          </Button>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 48, color: "#18B76C", mb: 2 }} />
                          <Typography fontWeight={700} fontSize={16} mb={1} color="#18B76C">
                            Image Uploaded Successfully
                          </Typography>
                          <Typography color="text.secondary" variant="body2" mb={1}>
                            {uploadedImage.name}
                          </Typography>
                          <Typography color="text.secondary" variant="body2" mb={2}>
                            {(uploadedImage.size / 1024 / 1024).toFixed(2)} MB
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveImage();
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            Remove & Upload New
                          </Button>
                        </>
                      )}
                    </Paper>

                    {/* Image Preview */}
                    {imagePreview && (
                      <Paper
                        elevation={0}
                        sx={{
                          width: 350,
                          minHeight: 300,
                          borderRadius: 3,
                          border: "1px solid #e0e0e0",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", bgcolor: "#f9fafb" }}>
                          <Typography fontWeight={600} fontSize={14}>
                            Preview
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            p: 2,
                            bgcolor: "#fafafa",
                          }}
                        >
                          <img
                            src={imagePreview}
                            alt="Preview"
                            style={{
                              maxWidth: "100%",
                              maxHeight: 250,
                              objectFit: "contain",
                              borderRadius: 4,
                            }}
                          />
                        </Box>
                      </Paper>
                    )}
                  </Box>

                  {/* ============================================================================ */}
                  {/* METRIC SELECTION UI */}
                  {/* ============================================================================ */}
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 4,
                      p: 3,
                      borderRadius: 3,
                      border: "1px solid #e0e0e0",
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                      <Typography fontWeight={700} fontSize={18}>
                        Select Metrics to Run
                      </Typography>
                      <Typography fontWeight={600} fontSize={14} color="primary">
                        {selectedMetricsCount} / {totalMetricsCount} metrics selected
                      </Typography>
                    </Box>

                    {/* Quick Selection Buttons by Risk Category */}
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAllMetrics}
                        sx={{ textTransform: "none", borderColor: "#0094FB", color: "#0094FB" }}
                      >
                        Select All
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleDeselectAllMetrics}
                        sx={{ textTransform: "none", borderColor: "#666", color: "#666" }}
                      >
                        Deselect All
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectSecurityMetrics}
                        sx={{ textTransform: "none", borderColor: "#e74c3c", color: "#e74c3c" }}
                      >
                        Security
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectAccuracyMetrics}
                        sx={{ textTransform: "none", borderColor: "#27ae60", color: "#27ae60" }}
                      >
                        Accuracy
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectPrivacyMetrics}
                        sx={{ textTransform: "none", borderColor: "#9b59b6", color: "#9b59b6" }}
                      >
                        Privacy/Ethics
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectFairnessMetrics}
                        sx={{ textTransform: "none", borderColor: "#f39c12", color: "#f39c12" }}
                      >
                        Fairness
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSelectRobustnessMetrics}
                        sx={{ textTransform: "none", borderColor: "#3498db", color: "#3498db" }}
                      >
                        Robustness
                      </Button>
                    </Box>

                    {/* Metrics Accordions by Category */}
                    <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                      {Object.entries(VISION_METRICS).map(([categoryName, metrics]) => (
                        <Accordion
                          key={categoryName}
                          defaultExpanded={categoryName === 'Security'}
                          sx={{
                            mb: 1,
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px !important",
                            "&:before": { display: "none" },
                            boxShadow: "none",
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor: isCategoryFullySelected(categoryName) ? "#E1F5FF" : "#f9fafb",
                              borderRadius: "8px",
                              minHeight: 48,
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                              <Checkbox
                                checked={isCategoryFullySelected(categoryName)}
                                indeterminate={isCategoryPartiallySelected(categoryName)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => handleToggleCategory(categoryName)}
                                sx={{ p: 0 }}
                              />
                              <Typography fontWeight={600} fontSize={14}>
                                {categoryName}
                              </Typography>
                              <Typography fontSize={12} color="text.secondary" sx={{ ml: "auto", mr: 2 }}>
                                {metrics.filter(m => selectedMetrics[m.id]).length} / {metrics.length}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 1 }}>
                            <Grid container spacing={1}>
                              {metrics.map((metric) => (
                                <Grid item xs={12} sm={6} md={4} key={metric.id}>
                                  <Tooltip title={metric.description} placement="top">
                                    <Box
                                      onClick={() => handleToggleMetric(metric.id)}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        p: 1,
                                        borderRadius: 1,
                                        cursor: "pointer",
                                        bgcolor: selectedMetrics[metric.id] ? "#E7F4FF" : "transparent",
                                        border: selectedMetrics[metric.id] ? "1px solid #0094FB" : "1px solid #e0e0e0",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                          bgcolor: selectedMetrics[metric.id] ? "#D1ECFF" : "#f5f5f5",
                                        },
                                      }}
                                    >
                                      <Checkbox
                                        checked={selectedMetrics[metric.id] || false}
                                        onChange={() => handleToggleMetric(metric.id)}
                                        size="small"
                                        sx={{ p: 0, mr: 1 }}
                                      />
                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography fontSize={12} fontWeight={600} noWrap>
                                          {metric.name}
                                        </Typography>
                                        <Typography fontSize={10} color="text.secondary">
                                          {metric.threshold}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Tooltip>
                                </Grid>
                              ))}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Box>
                  </Paper>

                  {/* Run Validation Button */}
                  <Box mt={4} display="flex" justifyContent="flex-start" alignItems="center" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      disabled={!uploadedImage || validationLoading || selectedMetricsCount === 0}
                      onClick={handleRunVisionValidation}
                      sx={{
                        minWidth: 280,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: "#0094FB",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 16,
                        "&:hover": {
                          backgroundColor: "#007FDB",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#D1D5DB",
                          color: "#9CA3AF",
                        },
                      }}
                    >
                      {validationLoading
                        ? "Running Validation..."
                        : `Run ${selectedMetricsCount} Selected Metric${selectedMetricsCount !== 1 ? 's' : ''}`}
                    </Button>
                    {selectedMetricsCount === 0 && (
                      <Typography fontSize={13} color="error.main">
                        Please select at least one metric
                      </Typography>
                    )}
                    {!uploadedImage && (
                      <Typography fontSize={13} color="text.secondary">
                        Upload an image first
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : isAgentic ? (
                /* ============================================================================ */
                /* A9/A12 AGENTIC - CSV Upload UI with Metric Selection */
                /* ============================================================================ */
                <Box>
                  <Typography variant="h6" fontWeight={600} mb={2}>
                    Upload CSV Data & Select Metrics
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={3}>
                    Upload a CSV file containing validation data for agentic AI metrics. Supported format: .csv (max 50MB)
                  </Typography>

                  {/* Hidden file input for CSV */}
                  <input
                    type="file"
                    ref={csvInputRef}
                    onChange={handleCSVUpload}
                    accept=".csv"
                    style={{ display: 'none' }}
                  />

                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                    {/* CSV Upload Area */}
                    <Paper
                      elevation={0}
                      onDragOver={handleCSVDragOver}
                      onDrop={handleCSVDrop}
                      onClick={() => !uploadedCSV && csvInputRef.current?.click()}
                      sx={{
                        flex: 1,
                        minHeight: 300,
                        borderRadius: 3,
                        border: uploadedCSV ? "2px solid #18B76C" : "2px dashed #B6E6FB",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 4,
                        bgcolor: uploadedCSV ? "#f0fff4" : "#F0F9FF",
                        cursor: uploadedCSV ? "default" : "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: uploadedCSV ? "#f0fff4" : "#E1F5FF",
                          borderColor: uploadedCSV ? "#18B76C" : "#0094FB",
                        },
                      }}
                    >
                      {!uploadedCSV ? (
                        <>
                          <Box
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "50%",
                              bgcolor: "#E1F5FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 2,
                            }}
                          >
                            <CloudUploadIcon sx={{ fontSize: 40, color: "#0094FB" }} />
                          </Box>
                          <Typography fontWeight={700} fontSize={18} mb={1}>
                            Drag & Drop CSV File Here
                          </Typography>
                          <Typography color="text.secondary" variant="body2" mb={2}>
                            or click to browse files
                          </Typography>
                          <Button
                            variant="outlined"
                            startIcon={<InsertDriveFileOutlinedIcon />}
                            sx={{
                              textTransform: "none",
                              borderColor: "#0094FB",
                              color: "#0094FB",
                              "&:hover": {
                                bgcolor: "#E1F5FF",
                                borderColor: "#0094FB",
                              },
                            }}
                          >
                            Select CSV File
                          </Button>
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon sx={{ fontSize: 48, color: "#18B76C", mb: 2 }} />
                          <Typography fontWeight={700} fontSize={16} mb={1} color="#18B76C">
                            CSV Uploaded Successfully
                          </Typography>
                          <Typography color="text.secondary" variant="body2" mb={1}>
                            {csvFileName}
                          </Typography>
                          <Typography color="text.secondary" variant="body2" mb={2}>
                            {(uploadedCSV.size / 1024).toFixed(1)} KB
                          </Typography>
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={handleRemoveCSV}
                            sx={{ textTransform: "none" }}
                          >
                            Remove & Upload Different File
                          </Button>
                        </>
                      )}
                    </Paper>

                    {/* Agentic Metrics Selection Panel */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1.5,
                        minHeight: 300,
                        maxHeight: 600,
                        borderRadius: 3,
                        border: "1px solid #E5E7EB",
                        p: 3,
                        bgcolor: "#FFFFFF",
                        overflowY: "auto",
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography fontWeight={700} fontSize={16}>
                          Select Validation Metrics ({selectedAgenticMetricsCount}/{totalAgenticMetricsCount})
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            size="small"
                            onClick={handleSelectAllAgenticMetrics}
                            sx={{ textTransform: 'none', fontSize: 12 }}
                          >
                            Select All
                          </Button>
                          <Button
                            size="small"
                            onClick={handleDeselectAllAgenticMetrics}
                            sx={{ textTransform: 'none', fontSize: 12 }}
                          >
                            Deselect All
                          </Button>
                        </Box>
                      </Box>

                      {/* Agentic Metrics by Category */}
                      {Object.entries(AGENTIC_METRICS).map(([category, metrics]) => (
                        <Accordion
                          key={category}
                          expanded={expandedAgenticCategories[category] !== false}
                          onChange={() => setExpandedAgenticCategories(prev => ({
                            ...prev,
                            [category]: !prev[category]
                          }))}
                          sx={{
                            boxShadow: 'none',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px !important',
                            mb: 1,
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor: isAgenticCategoryFullySelected(category) ? '#E1F5FF' : '#F9FAFB',
                              borderRadius: '8px',
                              '&.Mui-expanded': { borderRadius: '8px 8px 0 0' },
                            }}
                          >
                            <FormControlLabel
                              onClick={(e) => e.stopPropagation()}
                              control={
                                <Checkbox
                                  checked={isAgenticCategoryFullySelected(category)}
                                  indeterminate={isAgenticCategoryPartiallySelected(category)}
                                  onChange={() => handleToggleAgenticCategory(category)}
                                  sx={{ py: 0 }}
                                />
                              }
                              label={
                                <Typography fontWeight={600} fontSize={14}>
                                  {category} ({metrics.filter(m => selectedAgenticMetrics[m.id]).length}/{metrics.length})
                                </Typography>
                              }
                            />
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
                              {metrics.map((metric) => (
                                <Tooltip key={metric.id} title={metric.description} placement="left" arrow>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        size="small"
                                        checked={selectedAgenticMetrics[metric.id] || false}
                                        onChange={() => handleToggleAgenticMetric(metric.id)}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography fontSize={13}>{metric.name}</Typography>
                                        <Chip
                                          label={metric.threshold}
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: 10,
                                            bgcolor: '#F3F4F6',
                                          }}
                                        />
                                      </Box>
                                    }
                                    sx={{ m: 0 }}
                                  />
                                </Tooltip>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Paper>
                  </Box>

                  {/* Run Agentic Validation Button */}
                  <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      disabled={!uploadedCSV || agenticValidationLoading || selectedAgenticMetricsCount === 0}
                      onClick={handleRunAgenticValidation}
                      sx={{
                        minWidth: 280,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: "#0094FB",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 16,
                        "&:hover": {
                          backgroundColor: "#007FDB",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#D1D5DB",
                          color: "#9CA3AF",
                        },
                      }}
                    >
                      {agenticValidationLoading
                        ? "Running Validation..."
                        : `Run ${selectedAgenticMetricsCount} Selected Metric${selectedAgenticMetricsCount !== 1 ? 's' : ''}`}
                    </Button>
                    {selectedAgenticMetricsCount === 0 && (
                      <Typography fontSize={13} color="error.main">
                        Please select at least one metric
                      </Typography>
                    )}
                    {!uploadedCSV && (
                      <Typography fontSize={13} color="text.secondary">
                        Upload a CSV file first
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                /* ============================================================================ */
                /* A3 RAG - Test Scripts Generation (Original UI) */
                /* ============================================================================ */
                <>
              {!testsGenerated ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "stretch",
                    width: "100%",
                    mt: 2,
                  }}
                >
                  {/* Left card */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      minHeight: 260,
                      borderRadius: 2,
                      border: "1px solid #e6eef9",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        width: 76,
                        height: 76,
                        borderRadius: "50%",
                        bgcolor: "#eaf6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 34, color: "#0a6cff" }}
                      />
                    </Box>

                    <Typography fontWeight={700} fontSize={16} mb={1}>
                      Generated Tests
                    </Typography>
                    <Typography color="#3F4753" variant="body2" sx={{ mb: 1 }}>
                      No test cases yet
                    </Typography>
                  </Paper>

                  {/* Right card */}
                  <Paper
                    elevation={0}
                    sx={{
                      flex: 1,
                      minHeight: 260,
                      borderRadius: 2,
                      border: "1px solid #e6eef9",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 4,
                      bgcolor: "#fff",
                    }}
                  >
                    <Box
                      sx={{
                        width: 76,
                        height: 76,
                        borderRadius: "50%",
                        bgcolor: "#eaf6ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 34, color: "#0a6cff" }}
                      />
                    </Box>

                    <Typography fontWeight={700} fontSize={16} mb={1}>
                      No test cases yet
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body2"
                      sx={{ mb: 1 }}
                    >
                      Generate test cases and select one to view the script
                    </Typography>
                  </Paper>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 3,
                    alignItems: "flex-start",
                    flexWrap: "nowrap",
                    width: "100%",
                    mt: 2,
                  }}
                >
                  {/* --- Generated Tests --- */}
                  <Paper
                    sx={{
                      p: 3,
                      width: 280,
                      maxWidth: 280,
                      height: 520,
                      borderRadius: "16px",
                      flexShrink: 0,
                      boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
                      overflowY: "auto",
                    }}
                  >
                    <Typography
                      fontWeight={700}
                      variant="h6"
                      mb={2}
                      sx={{ fontSize: 18 }}
                    >
                      Generated Tests
                    </Typography>

                    {/* Test cards list */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {generatedTests.map((test, index) => (
                        <Box
                          key={index}
                          onClick={() => setSelectedTestIndex(index)}
                          sx={{
                            bgcolor:
                              selectedTestIndex === index
                                ? "#E7F7FF"
                                : "#F9FAFB",
                            border:
                              selectedTestIndex === index
                                ? "3px solid #33AAFF"
                                : "2px solid #E5E7EB",
                            borderRadius: "14px",
                            p: 2.2,
                            width: "100%",
                            boxSizing: "border-box",
                            cursor: "pointer",
                            transition: "all 0.2s",
                            "&:hover": {
                              borderColor: "#33AAFF",
                              bgcolor: "#F0F9FF",
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              color: "#22292F",
                              mb: 2,
                              lineHeight: "18px",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                            }}
                          >
                            {test.testName || `Test ${index + 1}`}
                          </Typography>

                          {/* Chips */}
                          <Box
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Box
                              sx={{
                                bgcolor: "#E7F1FF",
                                color: "#1A8DFF",
                                borderRadius: "12px",
                                px: 1.8,
                                py: 0.6,
                                fontWeight: 700,
                                fontSize: 10,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {test.framework || testFramework}
                            </Box>

                            <Box
                              sx={{
                                bgcolor: "#fff",
                                color: "#8CA0B3",
                                borderRadius: "12px",
                                px: 1.8,
                                py: 0.6,
                                fontWeight: 700,
                                fontSize: 10,
                                border: "2px solid #E7F1FF",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {test.metric || "Metric"}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>

                    {/* Framework Stats */}
                    <Box sx={{ mt: 3, width: "100%" }}>
                      <Typography fontWeight={700} mb={1} fontSize={15}>
                        Framework Stats
                      </Typography>
                      {(() => {
                        const frameworkCounts = {};
                        generatedTests.forEach((test) => {
                          const fw = test.framework || testFramework;
                          frameworkCounts[fw] = (frameworkCounts[fw] || 0) + 1;
                        });
                        return Object.entries(frameworkCounts).map(
                          ([fw, count]) => (
                            <Box
                              key={fw}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "#1A8DFF",
                                  fontWeight: 600,
                                  cursor: "pointer",
                                  fontSize: 14,
                                }}
                              >
                                {fw}
                              </Typography>
                              <Typography color="#8CA0B3" fontSize={14}>
                                {count}
                              </Typography>
                            </Box>
                          )
                        );
                      })()}
                    </Box>
                  </Paper>

                  {/* --- Test Details --- */}
                  <Paper
                    sx={{
                      p: 3,
                      minWidth: 320,
                      borderRadius: 3,
                      boxShadow: "0 2px 6px rgba(60,60,60,0.065)",
                      bgcolor: "#fff",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      mb={2}
                      color="text.primary"
                    >
                      Test Details
                    </Typography>

                    {selectedTestIndex >= 0 &&
                    generatedTests[selectedTestIndex] ? (
                      <>
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="body2"
                            color="#8CA0B3"
                            fontWeight={400}
                          >
                            Use Case
                          </Typography>
                          <Typography
                            variant="body2"
                            color="#22292F"
                            fontWeight={400}
                          >
                            {generatedTests[selectedTestIndex].useCase || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="body2"
                            color="#8CA0B3"
                            fontWeight={400}
                          >
                            Status
                          </Typography>
                          <Typography
                            variant="body2"
                            color="#18B76C"
                            fontWeight={400}
                          >
                            {generatedTests[selectedTestIndex].status ||
                              "Generated"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="body2"
                            color="#8CA0B3"
                            fontWeight={400}
                          >
                            Metric
                          </Typography>
                          <Typography
                            variant="body2"
                            color="#22292F"
                            fontWeight={400}
                          >
                            {generatedTests[selectedTestIndex].metric || "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="body2"
                            color="#8CA0B3"
                            fontWeight={400}
                          >
                            Threshold
                          </Typography>
                          <Typography
                            variant="body2"
                            color="#22292F"
                            fontWeight={400}
                          >
                            {generatedTests[selectedTestIndex].threshold ||
                              "N/A"}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography
                            variant="body2"
                            color="#8CA0B3"
                            fontWeight={400}
                          >
                            Input Prompt
                          </Typography>
                          <Typography
                            variant="body2"
                            color="#22292F"
                            fontWeight={400}
                          >
                            {generatedTests[selectedTestIndex].inputPrompt ||
                              "N/A"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="body2"
                            color="#8CA0B3"
                            fontWeight={400}
                          >
                            Expected Response
                          </Typography>
                          <Typography
                            variant="body2"
                            color="#22292F"
                            fontWeight={400}
                          >
                            {generatedTests[selectedTestIndex]
                              .expectedResponse || "N/A"}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography
                        variant="body2"
                        color="#8CA0B3"
                        textAlign="center"
                        py={4}
                      >
                        Select a test to view details
                      </Typography>
                    )}
                  </Paper>

                  {/* --- Script Code Block --- */}
                  <Paper
                    sx={{
                      minWidth: 410,
                      maxWidth: 430,
                      borderRadius: "12px",
                      bgcolor: "#fff",
                      boxShadow: "none",
                      p: 0,
                      overflow: "hidden",
                    }}
                  >
                    {selectedTestIndex >= 0 &&
                    generatedTests[selectedTestIndex] ? (
                      <>
                        {/* Unified Dark Header */}
                        <Box
                          sx={{
                            background: "#22292F",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1,
                            borderTopLeftRadius: "12px",
                            borderTopRightRadius: "12px",
                            color: "#fff",
                            fontSize: 15,
                            fontFamily: "monospace",
                          }}
                        >
                          {/* Filename */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <svg
                              style={{
                                width: 18,
                                height: 18,
                                verticalAlign: "middle",
                              }}
                              viewBox="0 0 24 24"
                            >
                              <path
                                fill="#88a0b3"
                                d="M10 4H2v16h20V8H12l-2-4z"
                              />
                            </svg>
                            <Typography
                              sx={{
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 15,
                                fontFamily: "monospace",
                              }}
                            >
                              /tests/
                              {generatedTests[
                                selectedTestIndex
                              ].testName?.replace(/\s+/g, "_") ||
                                `test_${selectedTestIndex}`}
                              .
                              {generatedTests[selectedTestIndex].framework ===
                              "Pytest"
                                ? "py"
                                : "js"}
                            </Typography>
                          </Box>
                          {/* Icons */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{ color: "#fff", p: 0.5 }}
                            >
                              <FiEdit2 style={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: "#fff", p: 0.5 }}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  generatedTests[selectedTestIndex].code || ""
                                );
                              }}
                            >
                              <FiCopy style={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{ color: "#AEE3FA", p: 0.5 }}
                              onClick={() => {
                                const element = document.createElement("a");
                                const file = new Blob(
                                  [
                                    generatedTests[selectedTestIndex].code ||
                                      "",
                                  ],
                                  { type: "text/plain" }
                                );
                                element.href = URL.createObjectURL(file);
                                element.download = `${
                                  generatedTests[
                                    selectedTestIndex
                                  ].testName?.replace(/\s+/g, "_") ||
                                  `test_${selectedTestIndex}`
                                }.${
                                  generatedTests[selectedTestIndex]
                                    .framework === "Pytest"
                                    ? "py"
                                    : "js"
                                }`;
                                document.body.appendChild(element);
                                element.click();
                                document.body.removeChild(element);
                              }}
                            >
                              <FiDownload style={{ fontSize: 19 }} />
                            </IconButton>
                          </Box>
                        </Box>

                        {/* Code Block */}
                        <Box
                          sx={{
                            background: "#141518",
                            color: "#bee6db",
                            borderBottomLeftRadius: "12px",
                            borderBottomRightRadius: "12px",
                            px: 2,
                            py: 1,
                            fontSize: 14,
                            fontFamily: "monospace",
                            height: 470,
                            lineHeight: 1.55,
                            overflowY: "auto",
                            overflowX: "hidden",
                            whiteSpace: "pre-wrap",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                          }}
                          component="pre"
                        >
                          {generatedTests[selectedTestIndex].code ||
                            "// No code generated"}
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ p: 4, textAlign: "center", color: "#8CA0B3" }}>
                        <Typography variant="body2">
                          Select a test to view the code
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box> /* closes main flex container for generated tests */
              )}

              {/* Run Selected Tests button at bottom right only in this tab */}
              <Box mt={4} display="flex" justifyContent="flex-start" gap={2}>
                {/* DOWNLOAD BUTTON */}
                {/* <Button
                  variant="outlined"
                  sx={{
                    width: "200px",
                    height: "40px",

                    paddingTop: "8px",
                    paddingBottom: "8px",
                    paddingLeft: "16px",
                    paddingRight: "16px",

                    borderRadius: "8px",
                    border: "2px solid #0094FB",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",

                    backgroundColor: "transparent",
                    opacity: 1,
                    color: "#0094FB",
                    fontWeight: 600,
                    fontSize: "16px",
                    textTransform: "none",

                    "&:hover": {
                      backgroundColor: "#E7F4FF",
                      borderColor: "#0094FB",
                    },
                  }}
                  onClick={() => {
                    // 🔽 TODO: Add your download logic here
                    console.log("Download clicked");
                  }}
                >
                  Download
                </Button> */}

                {/* RUN SELECTED TESTS BUTTON (unchanged except layout) */}
                <Button
                  variant="contained"
                  sx={{
                    width: "247px",
                    height: "40px",

                    paddingTop: "8px",
                    paddingBottom: "8px",
                    paddingLeft: "16px",
                    paddingRight: "16px",

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",

                    borderRadius: "8px",
                    border: "2px solid #0094FB",

                    backgroundColor: "#0094FB",
                    opacity: 0.5,

                    fontWeight: 600,
                    fontSize: "16px",
                    textTransform: "none",

                    color: "#FFFFFF",

                    "&:hover": {
                      opacity: 0.8,
                      backgroundColor: "#0094FB",
                      borderColor: "#0094FB",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#D1D5DB",
                      color: "#9CA3AF",
                      border: "2px solid #E5E7EB",
                    },
                  }}
                  onClick={async () => {
                    // Pre-flight validation
                    const dataset = testDataset.length > 0 ? testDataset : qaData;
                    if (!dataset || dataset.length === 0) {
                      setError("No test data available. Please ensure dataset is loaded before running tests.");
                      return;
                    }

                    if (!previousStage.apiEndpoint) {
                      setError("API endpoint is not configured. Please check the application setup.");
                      return;
                    }

                    // Clear any previous errors
                    setError("");
                    setTabValue(1);

                    // 1️⃣ Start backend call
                    const backendResults = await handleRunSelectedTests();

                    // 2️⃣ STOP loading overlay BEFORE animation starts
                    setLoading(false);

                    // 3️⃣ NOW start animation
                    setIsExecuting(true);
                    await simulateExecution(backendResults);
                    setIsExecuting(false);
                  }}
                  disabled={
                    loading ||
                    initialLoading ||
                    (testDataset.length === 0 && qaData.length === 0) ||
                    !previousStage.apiEndpoint
                  }
                >
                  {loading
                    ? "Running..."
                    : initialLoading
                    ? "Loading..."
                    : (testDataset.length === 0 && qaData.length === 0)
                    ? "No Data Available"
                    : "Run Selected Tests"}
                </Button>
              </Box>
              </>
              )}
            </Box>
          )}

          {/* ---- TEST EVALUATION TAB ---- */}
          {tabValue === 1 && (
            <Box>
              {error && (
                <Box
                  mb={3}
                  sx={{
                    p: 2,
                    bgcolor: "#ffeaea",
                    borderRadius: 2,
                    border: "1px solid #D90000",
                  }}
                >
                  <Typography color="#D90000" fontWeight={600}>
                    ❌ Error: {error}
                  </Typography>
                </Box>
              )}

              {results.length === 0 && !loading ? (
                <Box sx={{ p: 10, textAlign: "center", color: "#888" }}>
                  <Typography variant="h5" color="text.secondary">
                    No test results yet.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Click "Run Selected Tests" in the Test Scripts Generation
                    tab to start evaluation.
                  </Typography>
                </Box>
              ) : (
                <>
                
                  {/* <Paper sx={{ p: 3, mb: 2, boxShadow: "none" }}>
                    <Typography fontWeight={700} fontSize={22} mb={1}>
                      Execution Progress
                    </Typography>
                    <LinearProgress
                      sx={{
                        height: 6,
                        borderRadius: 2,
                        mb: 3,
                        bgcolor: "#dde4df",
                      }}
                      value={progress}
                        
                      color="success"
                      variant="determinate"
                    />
                    <Grid
                      container
                      spacing={2}
                      justifyContent="space-between"
                      alignItems="stretch"
                      sx={{ width: "100%", mx: 0 }}
                    >
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Paper
                          sx={{
                            p: 2,
                            minHeight: 90,
                            textAlign: "center",
                            boxShadow: "none",
                            border: "1.5px solid #e7e7e7",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            fontSize={16}
                            color="text.secondary"
                            mb={0.5}
                            fontWeight={700}
                          >
                            Total
                          </Typography>
                          <Typography fontWeight={700} fontSize={28}>
                            {summary?.total ?? "--"}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Paper
                          sx={{
                            p: 2,
                            minHeight: 90,
                            textAlign: "center",
                            boxShadow: "none",
                            border: "1.5px solid #e7e7e7",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            fontSize={16}
                            color="text.secondary"
                            mb={0.5}
                            fontWeight={700}
                          >
                            Passed
                          </Typography>
                          <Typography
                            fontWeight={700}
                            fontSize={28}
                            sx={{ color: "#18B76C" }}
                          >
                            {summary?.passed ?? "--"}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Paper
                          sx={{
                            p: 2,
                            minHeight: 90,
                            textAlign: "center",
                            boxShadow: "none",
                            border: "1.5px solid #e7e7e7",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            fontSize={16}
                            color="text.secondary"
                            mb={0.5}
                            fontWeight={700}
                          >
                            Failed
                          </Typography>
                          <Typography
                            fontWeight={700}
                            fontSize={28}
                            sx={{ color: "#D90000" }}
                          >
                            {summary?.failed ?? "--"}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Paper
                          sx={{
                            p: 2,
                            minHeight: 90,
                            textAlign: "center",
                            boxShadow: "none",
                            border: "1.5px solid #e7e7e7",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            fontSize={16}
                            color="text.secondary"
                            mb={0.5}
                            fontWeight={700}
                          >
                            Running
                          </Typography>
                          <Typography
                            fontWeight={700}
                            fontSize={28}
                            sx={{ color: "#007FDB" }}
                          >
                            {summary?.running ?? "0"}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2.4}>
                        <Paper
                          sx={{
                            p: 2,
                            minHeight: 90,
                            textAlign: "center",
                            boxShadow: "none",
                            border: "1.5px solid #e7e7e7",
                            borderRadius: 2,
                          }}
                        >
                          <Typography
                            fontSize={16}
                            color="text.secondary"
                            mb={0.5}
                            fontWeight={700}
                          >
                            Completed
                          </Typography>
                          <Typography
                            fontWeight={700}
                            fontSize={28}
                            sx={{ color: "#007FDB" }}
                          >
                            {summary?.completed ?? summary?.total ?? "--"}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper> */}


                  <Paper sx={{ p: 3, mb: 2, boxShadow: "none" }}>
                  <Typography fontWeight={700} fontSize={22} mb={1}>
                    Execution Progress
                  </Typography>

                  {/* Real-time test execution status */}
                  {isExecuting && currentTestIndex > 0 && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "#F0F9FF", borderRadius: 2, border: "1px solid #B6E6FB" }}>
                      <Typography fontWeight={600} fontSize={16} color="#0094FB" mb={0.5}>
                        Calculating metrices {currentTestIndex} of {totalCount}
                      </Typography>
                      <Typography fontSize={14} color="text.secondary" sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis"
                      }}>
                        {currentTestName}
                      </Typography>
                    </Box>
                  )}

                  <LinearProgress
                    sx={{ height: 6, borderRadius: 2, mb: 3, bgcolor: "#dde4df" }}
                    value={progress}
                    color="success"
                    variant="determinate"
/>
       <Grid container spacing={2} justifyContent="space-between" alignItems="stretch" sx={{ width: "100%", mx: 0 }}>
 
  {/* TOTAL */}
  <Grid item xs={12} sm={6} md={2.4}>
    <Paper sx={{ p: 2, minHeight: 90, textAlign: "center", border: "1.5px solid #e7e7e7", borderRadius: 2 }}>
      <Typography fontSize={16} color="text.secondary" fontWeight={700}>Total Runs</Typography>
      <Typography fontWeight={700} fontSize={28}>
        {summary ? summary.passed + summary.failed : "--"}
      </Typography>
    </Paper>
  </Grid>
 
  {/* PASSED */}
  <Grid item xs={12} sm={6} md={2.4}>
    <Paper sx={{ p: 2, minHeight: 90, textAlign: "center", border: "1.5px solid #e7e7e7", borderRadius: 2 }}>
      <Typography fontSize={16} color="text.secondary" fontWeight={700}>Passed</Typography>
      <Typography fontWeight={700} fontSize={28} sx={{ color: "#18B76C" }}>
        {summary ? summary.passed : "--"}
      </Typography>
    </Paper>
  </Grid>
 
  {/* FAILED */}
  <Grid item xs={12} sm={6} md={2.4}>
    <Paper sx={{ p: 2, minHeight: 90, textAlign: "center", border: "1.5px solid #e7e7e7", borderRadius: 2 }}>
      <Typography fontSize={16} color="text.secondary" fontWeight={700}>Failed</Typography>
      <Typography fontWeight={700} fontSize={28} sx={{ color: "#D90000" }}>
        {summary ? summary.failed : "--"}
      </Typography>
    </Paper>
  </Grid>
 
  {/* RUNNING
  <Grid item xs={12} sm={6} md={2.4}>
    <Paper sx={{ p: 2, minHeight: 90, textAlign: "center", border: "1.5px solid #e7e7e7", borderRadius: 2 }}>
      <Typography fontSize={16} color="text.secondary" fontWeight={700}>Running</Typography>
      <Typography fontWeight={700} fontSize={28} sx={{ color: "#007FDB" }}>
        {isExecuting ? runningCount : 0}
      </Typography>
    </Paper>
  </Grid> */}
 
  {/* COMPLETED */}
  <Grid item xs={12} sm={6} md={2.4}>
    <Paper sx={{ p: 2, minHeight: 90, textAlign: "center", border: "1.5px solid #e7e7e7", borderRadius: 2 }}>
      <Typography fontSize={16} color="text.secondary" fontWeight={700}>Completed</Typography>
      <Typography fontWeight={700} fontSize={28} sx={{ color: "#007FDB" }}>
         {/* {isExecuting ? `${completedCount}/${totalCount}` : summary?.completedCount } */}
         {isExecuting
  ? `${completedCount}/${totalCount}`
  : summary
  ? `${summary.passed + summary.failed}/${summary.passed + summary.failed}`
  : "--"}

      </Typography>
    </Paper>
  </Grid>
 
</Grid>
 
 
 
                </Paper>
 

 {/* === AVERAGE SCORES WITH SMART ROW CONTROL + SCROLL === */}
 {/* Hide this section for VISION archetype - show only for other test types */}
{summary?.averages && !isVision && (
  <Box
    sx={{
      mt: 3,
      mb: 3,
      px: 0,
      display: "flex",
      flexWrap: "wrap",
      gap: 2,
 
      // Enable vertical scroll only if > 6 boxes
      maxHeight:
        summary.averages &&
        Object.keys(summary.averages).length > 6
          ? "500px"
          : "auto",
 
      overflowY:
        summary.averages &&
        Object.keys(summary.averages).length > 6
          ? "auto"
          : "visible",
    }}
  >
    {Object.entries(summary.averages).map(([metricName, avg], idx) => (
      <Box
        key={idx}
        sx={{
          width: "300px",
          height: "320px",
          borderRadius: "8px",
          border: "0.75px solid #D9D9D9",
          background: "#FFFFFF",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Typography fontWeight={700} fontSize={18}>
          {metricName}
        </Typography>
 
        {/* <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
          <Box>
            <Typography fontSize={14} color="text.secondary">
               Runs for this Metric
            </Typography>
            <Typography fontWeight={700} fontSize={22}>
              {avg?.toFixed?.(3) ?? "--"}
            </Typography>
          </Box>
 
          <Box sx={{ textAlign: "right" }}>
            <Typography fontSize={14} color="text.secondary">
              Pass Rate
            </Typography>
            <Typography fontWeight={700} fontSize={20}>
              {(() => {
                const total = results.filter(
                  (r) => r.metric === metricName
                ).length;
                const passed = results.filter(
                  (r) =>
                    r.metric === metricName && r.status === "Pass"
                ).length;
                return total
                  ? `${Math.round((passed / total) * 100)}%`
                  : "--";
              })()}
            </Typography>
          </Box>
        </Box> */}


        <Box sx={{ mt: 2 }}>
            <Typography fontSize={14} color="text.secondary">
              Runs for this Metric
            </Typography>

            <Box
              sx={{
                mt: 1,
                pr: 1,
              }}
            >
              {results
                .filter((r) => r.metric === metricName)
                .map((r, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                      fontSize: 14,
                    }}
                  >
                    <Typography>
                      Run {index + 1} – {r.status}
                    </Typography>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: r.status === "Pass" ? "#18B76C" : "#D90000",
                      }}
                    >
                      {r.score}
                    </Typography>
                  </Box>
                ))}
            </Box>
          </Box>

 
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 2,
          }}
        >
          <Box>
            <Typography fontSize={14} color="text.secondary">
              Passed
            </Typography>
            <Typography fontWeight={700} fontSize={18} color="#18B76C">
              {
                results.filter(
                  (r) =>
                    r.metric === metricName && r.status === "Pass"
                ).length
              }
            </Typography>
          </Box>
 
          <Box>
            <Typography fontSize={14} color="text.secondary">
              Failed
            </Typography>
            <Typography fontWeight={700} fontSize={18} color="#D90000">
              {
                results.filter(
                  (r) =>
                    r.metric === metricName && r.status === "Fail"
                ).length
              }
            </Typography>
          </Box>
        </Box>
      </Box>
    ))}
  </Box>
)}



                  

                                {/* Tabbed Results View - Trust Score & NIST Compliance */}
                <Paper sx={{ mt: 4, boxShadow: "none" }}>
                  {/* Tabs for switching between views */}
                  {isVision && validationResults.length > 0 && (
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                      <Tabs
                        value={complianceTabValue}
                        onChange={(e, newVal) => setComplianceTabValue(newVal)}
                        sx={{
                          '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 14,
                            minHeight: 48,
                          },
                          '& .Mui-selected': {
                            color: '#1e3a8a !important',
                          },
                          '& .MuiTabs-indicator': {
                            backgroundColor: '#1e3a8a',
                          },
                        }}
                      >
                        <Tab label="📊 Trust Score Results" />
                        <Tab label="📋 NIST AI RMF Compliance Matrix" />
                      </Tabs>
                    </Box>
                  )}

                  {/* Trust Score Results Tab */}
                  {(!isVision || complianceTabValue === 0) && (
                    <>
                  <Typography fontWeight={700} fontSize={23} p={2} pb={1}>
                    Trust Score Results{results.length > 0 ? ` (${results.length})` : ""}
                  </Typography>
 
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 3,
                      alignItems: "center",
                      px: 2,
                      pb: 1,
                      mt: 1,
                      color: "#949494",
                    }}
                  >
                    <TextField
                size="small"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="search">
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "80%", md: 220 } }}
              />
 
    

                      {/* <Button
                        variant="outlined"
                        sx={{
                          px: 2.5,
                          py: 1,
                          fontWeight: 600,
                          fontSize: 15,
                          borderRadius: 2,
                          color: "#1BA1F9",
                          borderColor: "#B6E6FB",
                          background: "#F7F8FA",
                          textTransform: "none",
                        }}
                        onClick={handleExport}
                        startIcon={
                          <span
                            className="material-icons"
                            style={{ fontSize: 22, color: "#1BA1F9" }}
                          ></span>
                        }
                      >
                        Export Entries
                      </Button> */}
                    </Box>

                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Use Case</TableCell>
                            {/* <TableCell>Metric</TableCell> */}
                            <TableCell>Metric</TableCell>
                            <TableCell>Score</TableCell>
                            <TableCell>Threshold</TableCell>
                            <TableCell>Status</TableCell>
                            {/* <TableCell>Time</TableCell> */}
                            <TableCell>Eval Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {results
                            .filter((row) => {
                              if (!searchQuery) return true;
                              const search = searchQuery.toLowerCase();
                              return (
                                (row.use_case || "")
                                  .toLowerCase()
                                  .includes(search) ||
                                (row.metric || "")
                                  .toLowerCase()
                                  .includes(search) ||
                                (row.risk || "").toLowerCase().includes(search)
                              );
                            })
                            .map((row, idx) => (
                              <TableRow key={idx}>
                                <TableCell>
                                  <Typography
                                    color="#1888d2"
                                    sx={{ cursor: "pointer" }}
                                  >
                                    {row.use_case}
                                  </Typography>
                                </TableCell>
                                {/* <TableCell>{row.risk || "--"}</TableCell> */}
                                <TableCell>{row.metric}</TableCell>
                                <TableCell>{row.score}</TableCell>
                                {/* <TableCell>{row.threshold}</TableCell> */}
                                <TableCell>{row.threshold_type || ">="} {row.threshold}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={row.status}
                                    color={
                                      row.status === "Pass"
                                        ? "success"
                                        : "error"
                                    }
                                    sx={{
                                      fontWeight: 700,
                                      background:
                                        row.status === "Pass"
                                          ? "#e5f6ef"
                                          : "#ffeaea",
                                      color:
                                        row.status === "Pass"
                                          ? "#22C872"
                                          : "#D90000",
                                      borderRadius: 1,
                                      px: 2,
                                    }}
                                    variant="outlined"
                                  />
                                </TableCell>
                                {/* <TableCell>{row.time || "--"}</TableCell>
                                 */}
                                <TableCell>{row.eval_type || "LLM+Code"}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    </>
                  )}

                  {/* NIST AI RMF Compliance Matrix Tab */}
                  {isVision && complianceTabValue === 1 && validationResults.length > 0 && (
                    <Box sx={{ p: 2 }}>
                      <Typography fontWeight={700} fontSize={20} color="#1e3a8a" mb={1}>
                        📋 NIST AI RMF COMPLIANCE MATRIX
                      </Typography>
                      <Typography fontSize={13} color="text.secondary" mb={2}>
                        Detailed Module Performance | Click to Expand Metrics | Threshold & Obtained Values | Status Tracking
                      </Typography>

                      {/* Compliance Table */}
                      <TableContainer sx={{ maxHeight: 600, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow sx={{ '& th': { backgroundColor: '#1e3a8a', color: 'white', fontWeight: 700 } }}>
                              <TableCell sx={{ width: 40 }}></TableCell>
                              <TableCell>Module / Metric</TableCell>
                              <TableCell align="center">Threshold</TableCell>
                              <TableCell align="center">Obtained</TableCell>
                              <TableCell align="center">Status</TableCell>
                              <TableCell>Remarks</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {validationResults.map((result, idx) => {
                              const moduleId = result.module_id;
                              const isExpanded = expandedModules[moduleId];
                              const details = result.details || {};

                              // Get all metric keys from details (excluding metadata)
                              const metadataKeys = ['module', 'media_type', 'execution_time', 'timestamp', 'error', 'risk_level'];
                              const metricKeys = Object.keys(details).filter(k => !metadataKeys.includes(k));

                              return (
                                <React.Fragment key={idx}>
                                  {/* Module Row (expandable) */}
                                  <TableRow
                                    sx={{
                                      backgroundColor: '#f0f4ff',
                                      cursor: 'pointer',
                                      '&:hover': { backgroundColor: '#e0e7ff' }
                                    }}
                                    onClick={() => setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }))}
                                  >
                                    <TableCell>
                                      <IconButton size="small">
                                        <ExpandMoreIcon sx={{
                                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                          transition: 'transform 0.2s'
                                        }} />
                                      </IconButton>
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontWeight={600} fontSize={14}>
                                        {result.metric || result.metric_name || moduleId?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                      </Typography>
                                      <Typography fontSize={11} color="text.secondary">
                                        {metricKeys.length} metrics (click ▶ to expand)
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography fontSize={13}>
                                        {THRESHOLD_CONFIG[moduleId]?.score?.label || '< 0.70'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Typography fontSize={13} fontWeight={600}>
                                        {typeof result.score === 'number' ? result.score.toFixed(3) : result.score}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        label={result.status === 'PASS' ? '✓ PASS' : result.status === 'FAIL' ? '✗ FAIL' : result.status}
                                        size="small"
                                        sx={{
                                          fontWeight: 600,
                                          backgroundColor: result.status === 'PASS' ? '#dcfce7' : result.status === 'FAIL' ? '#fee2e2' : '#f3f4f6',
                                          color: result.status === 'PASS' ? '#166534' : result.status === 'FAIL' ? '#991b1b' : '#6b7280',
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography fontSize={12} color={result.status === 'PASS' ? 'success.main' : 'error.main'}>
                                        {result.status === 'PASS' ? 'Within acceptable limits' : 'Review required'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>

                                  {/* Expanded Metrics Rows */}
                                  {isExpanded && metricKeys.map((metricKey, mIdx) => {
                                    const obtainedValue = details[metricKey];
                                    const compliance = checkMetricCompliance(moduleId, metricKey, obtainedValue);

                                    return (
                                      <TableRow
                                        key={`${idx}-${mIdx}`}
                                        sx={{ backgroundColor: '#fafafa' }}
                                      >
                                        <TableCell></TableCell>
                                        <TableCell>
                                          <Typography fontSize={12} color="text.secondary" sx={{ pl: 3 }}>
                                            └─ {metricKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Typography fontSize={12} color="text.secondary">
                                            {compliance.threshold || 'N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Typography fontSize={12}>
                                            {formatMetricValue(obtainedValue)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                          <Typography
                                            fontSize={11}
                                            fontWeight={600}
                                            color={compliance.status === 'PASS' ? 'success.main' : compliance.status === 'FAIL' ? 'error.main' : 'text.secondary'}
                                          >
                                            {compliance.status === 'PASS' ? '✓ PASS' : compliance.status === 'FAIL' ? '✗ FAIL' : '○ N/A'}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          <Typography fontSize={11} color="text.secondary">
                                            {compliance.remarks}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Export Buttons */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FiDownload />}
                          onClick={() => {
                            // Export to CSV functionality
                            const csvContent = validationResults.map(r => {
                              const details = r.details || {};
                              const metricKeys = Object.keys(details).filter(k => !['module', 'media_type', 'execution_time', 'timestamp', 'error', 'risk_level'].includes(k));
                              return metricKeys.map(mk => {
                                const comp = checkMetricCompliance(r.module_id, mk, details[mk]);
                                return `${r.module_id},${mk},${comp.threshold},${formatMetricValue(details[mk])},${comp.status},${comp.remarks}`;
                              }).join('\n');
                            }).join('\n');
                            const blob = new Blob([`Module,Metric,Threshold,Obtained,Status,Remarks\n${csvContent}`], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = 'nist_compliance_matrix.csv';
                            a.click();
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Export to CSV
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FiCopy />}
                          onClick={() => {
                            // Copy table to clipboard
                            const text = validationResults.map(r => `${r.metric || r.module_id}: ${r.status}`).join('\n');
                            navigator.clipboard.writeText(text);
                          }}
                          sx={{ textTransform: 'none' }}
                        >
                          Copy Table
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Paper>
                </>
              )}
            </Box>
          )}

          {/* Next Button */}
          {/* Next Button — show ONLY in Test Evaluation tab */}
          {tabValue === 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 4 }}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "#36a9fcff",
                  textTransform: "none",
                  px: 4,
                  py: 1.5,
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "#005252" },
                }}
                onClick={() => {
                  navigate(`/project/${projectId}/test-score-computation`);
                }}
              >
                Compute Trust Score
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Loading Overlays */}
      <LoadingOverlay open={genLoading} message="Generating Test Scripts..." />
      <LoadingOverlay open={loading} message="Running tests, please wait..." />
      <LoadingOverlay open={validationLoading} message="Running VISION validation modules..." />
      <LoadingOverlay open={agenticValidationLoading} message="Running AGENTIC validation metrics..." />
    </Box>
  );
}
